import { P } from 'components'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Edit2, Plus, Trash2 } from 'react-feather'
import { Button, Spinner } from 'reactstrap'
import ModalAddEditMapSchema from '../map-schema/ModalAddEditMapSchema'
import {
  useDeleteMapSchemaMutation,
  useFetchMapSchemasQuery,
} from '../map-schema/mapSchemaApiSlice'

const TabSchemaList = ({ itemId, itemName }) => {
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useIsMobile()

  const [isOpenModalAddEdit, setIsOpenModalAddEdit] = useState(false)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [dataSchemaById, setDataSchemaById] = useState(null)

  const [deleteMapSchema, { isLoading: isDeleting }] =
    useDeleteMapSchemaMutation()

  const { data, isLoading, refetch, isFetching } = useFetchMapSchemasQuery(
    { itemId },
    { refetchOnMountOrArgChange: true, skip: !itemId },
  )

  // HANDLE SUBMIT REMOVE BY ID
  const handleSubmitRemoveById = async () => {
    try {
      if (!dataSchemaById?.id) {
        enqueueSnackbar('Data skema tidak valid untuk dihapus', {
          variant: 'error',
        })
        return
      }

      const response = await deleteMapSchema(dataSchemaById.id)

      if (response && response?.data) {
        enqueueSnackbar(
          `Skema "${dataSchemaById?.createMethodLabel}" berhasil dihapus`,
          {
            variant: 'success',
          },
        )
        setIsOpenDeleteModal(false)
        setDataSchemaById(null)
        refetch()
      }
    } catch (error) {
      enqueueSnackbar('Skema gagal dihapus', {
        variant: 'error',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner />
        <p className="mt-2 text-muted">Memuat skema produksi...</p>
      </div>
    )
  }

  const schemas = data?.items || []

  const columns = [
    {
      name: <span>Nama Skema</span>,
      selector: (row) => row?.createMethodLabel,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <span className="fw-semibold">{row?.createMethodLabel || '-'}</span>
      ),
    },
    {
      name: <span>Jumlah Bahan Baku</span>,
      selector: (row) => row?.numInput,
      width: '200px',
      center: true,
      cell: (row) => <P size="small">{row?.numInput || 0}</P>,
    },
    {
      name: <span>Jumlah Barang Jadi</span>,
      selector: (row) => row?.numOutput,
      width: '200px',
      center: true,
      cell: (row) => <P size="small">{row?.numOutput || 0}</P>,
    },
    {
      name: <span className="fw-semibold"></span>,
      width: '120px',
      grow: 2,
      right: true,
      cell: (row, index) => (
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ gap: '12px' }}
          data-testid={`master-item-table-row-${row?.id}`}
        >
          <div
            id={`edit-schema-${index}`}
            data-testid={`master-item-row-edit-${row?.id}`}
          >
            <Edit2
              size={16}
              onClick={(e) => {
                e.stopPropagation()
                setDataSchemaById(row)
                setIsOpenModalAddEdit(true)
              }}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <PMBTooltip target={`edit-schema-${index}`} text="Edit" />

          <div
            id={`remove-schema-${index}`}
            data-testid={`master-item-row-delete-${row?.id}`}
          >
            <Trash2
              size={16}
              onClick={(e) => {
                e.stopPropagation()
                setIsOpenDeleteModal(true)
                setDataSchemaById(row)
              }}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <PMBTooltip target={`remove-schema-${index}`} text="Hapus" />
        </div>
      ),
    },
  ]

  return (
    <div>
      {/* MODAL ADD/EDIT MAP SCHEMA */}
      <ModalAddEditMapSchema
        isOpen={isOpenModalAddEdit}
        toggle={() => {
          setIsOpenModalAddEdit((prev) => !prev)
          setDataSchemaById(null)
        }}
        onSuccess={refetch}
        dataSchemaById={dataSchemaById}
        prefilledItemId={itemId}
        itemName={itemName}
      />

      {/* MODAL REMOVE MAP SCHEMA */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal((prev) => !prev)
          setDataSchemaById(null)
        }}
        title={`Hapus Skema Produksi`}
        dataId={dataSchemaById?.id}
        desc={`Apakah Anda yakin ingin menghapus skema "${dataSchemaById?.createMethodLabel}"?`}
        isLoading={isDeleting}
        onRemoveData={handleSubmitRemoveById}
        buttonTitle="Hapus"
      />

      <div
        className={`d-flex gap-2 ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-3`}
      >
        <div className={isMobile ? 'mb-2' : ''}>
          <h6 className={`fw-semibold mb-1 ${isMobile ? 'fs-6' : ''}`}>
            Daftar Map Skema
          </h6>
          {!isMobile && (
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
              Kelola skema dengan sistem versioning otomatis. Produksi selalu
              menggunakan versi terbaru.
            </p>
          )}
        </div>
        {schemas?.length ? (
          <Button
            onClick={() => setIsOpenModalAddEdit(true)}
            size="sm"
            color="primary"
            className="d-flex align-items-center justify-content-center gap-2"
            style={isMobile ? { width: '100%' } : {}}
          >
            <Plus size={16} />
            Tambah Skema
          </Button>
        ) : null}
      </div>

      {isFetching && (
        <div className="text-center py-3">
          <Spinner size="sm" color="primary" />
        </div>
      )}

      {!isFetching && schemas.length === 0 ? (
        <div className={`text-center ${isMobile ? 'py-4' : 'py-5'}`}>
          <div
            className="mx-auto mb-3"
            style={{
              width: isMobile ? '60px' : '80px',
              height: isMobile ? '60px' : '80px',
              borderRadius: '50%',
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Plus size={isMobile ? 30 : 40} color="#999" />
          </div>
          <h6 className={`fw-semibold mb-2 ${isMobile ? 'fs-6' : ''}`}>
            Belum ada map skema yang tersedia
          </h6>
          <p
            className={`text-muted mb-3 ${isMobile ? 'small' : ''}`}
            style={{ fontSize: isMobile ? '0.875rem' : '0.9rem' }}
          >
            Tambahkan skema komposisi untuk item <strong>{itemName}</strong>
          </p>
          {!isMobile && (
            <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
              Map skema mendefinisikan komposisi bahan dan cara produksi item
            </p>
          )}
          <Button
            onClick={() => setIsOpenModalAddEdit(true)}
            size="sm"
            color="primary"
            style={isMobile ? { width: '100%' } : {}}
          >
            <Plus size={16} className="me-2" />
            Tambah Skema Pertama
          </Button>
        </div>
      ) : (
        <DataTable
          responsive
          persistTableHead
          columns={columns}
          data={schemas}
          progressPending={isFetching}
          highlightOnHover
          pointerOnHover
          customStyles={{
            rows: {
              style: {
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                },
              },
            },
          }}
          progressComponent={
            <div style={{ margin: '50px 0px' }}>
              <Spinner color="primary" />
            </div>
          }
          noDataComponent={
            <p className="my-3 fw-semibold">
              Belum ada map skema yang tersedia
            </p>
          }
        />
      )}
    </div>
  )
}

export default TabSchemaList
